import React, { useState, useEffect, useMemo, useRef } from "react";
import { Flex, Text, Select, Button, Dialog, TextField } from "@radix-ui/themes";
import { ActiveIntegrateCard } from "components";
import {
  useGetSevenRoomsMappings,
  useGetSevenRoomsVenues,
  useSaveSevenRoomsMappings,
  useUpdateSevenRooms,
  useDeactivateSevenRooms,
  useUpdateSevenRoomsSyncSettings,
  useUpdateSevenRoomsClientTagSettings,
  useGetAllBrands,
} from "hooks";
import { useQueryClient } from "@tanstack/react-query";
import { useProfileContext, useLocationContext } from "context";
import { useToast } from "../../../context/ToastContext";
import { PermissionAlertDialog } from "components";
import { SevenRoomsDataFieldsSection, SevenRoomsLocationPair, SevenRoomsLocationPairHeaders, SevenRoomsReadonlyField } from "./SevenRoomsDataFieldsSection";
import { SevenRoomsSyncDirectionsSection } from "./SevenRoomsPullSection";
import { SevenRoomsPullScopeDialog } from "./SevenRoomsPullScopeDialog";
import { SevenRoomsPushScopeDialog } from "./SevenRoomsPushScopeDialog";
import {
  SevenRoomsClientTagSection,
  SevenRoomsRequiredFieldsNotice,
} from "./SevenRoomsClientTagSection";
import { normalizeSevenRoomsFieldMappings } from "./sevenroomsFieldMappings";
import "./sevenroomsLayout.css";

const ENVIRONMENT_OPTIONS = [
  { value: "demo", label: "Demo" },
  { value: "us", label: "United States" },
  { value: "eu", label: "Europe" },
];

export function ActiveSevenRoomsIntegration() {
  const queryClient = useQueryClient();
  const { userProfile } = useProfileContext();
  const { initialItems, locationsData } = useLocationContext();
  const { setToastMessage } = useToast();
  const [showPermissionAlert, setShowPermissionAlert] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isDeactivating, setIsDeactivating] = useState(false);
  const isOwner = userProfile.user_role === "Owner";

  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [environment, setEnvironment] = useState("demo");
  const [originalClientId, setOriginalClientId] = useState("");
  const [originalClientSecret, setOriginalClientSecret] = useState("");
  const [originalEnvironment, setOriginalEnvironment] = useState("demo");
  const [isCredentialsSaving, setIsCredentialsSaving] = useState(false);
  const [credentialsValid, setCredentialsValid] = useState(true);
  const [credentialsMessage, setCredentialsMessage] = useState(null);

  const [fieldMappings, setFieldMappings] = useState(() => normalizeSevenRoomsFieldMappings(null));
  const [pushEnabled, setPushEnabled] = useState(true);
  const [pullEnabled, setPullEnabled] = useState(false);
  const [pushScopeDialogOpen, setPushScopeDialogOpen] = useState(false);
  const [pullScopeDialogOpen, setPullScopeDialogOpen] = useState(false);
  const [isSyncSettingsSaving, setIsSyncSettingsSaving] = useState(false);
  const [isBrandsSaving, setIsBrandsSaving] = useState(false);
  const [isIndependentSaving, setIsIndependentSaving] = useState(false);
  const [clientTagGroup, setClientTagGroup] = useState("");
  const [clientTagName, setClientTagName] = useState("");
  const [originalClientTagGroup, setOriginalClientTagGroup] = useState("");
  const [originalClientTagName, setOriginalClientTagName] = useState("");
  const [isClientTagSaving, setIsClientTagSaving] = useState(false);

  const { data: brandsData, isLoading: isBrandsLoading } = useGetAllBrands(userProfile?.organisation_id);
  const { data: mappingsData } = useGetSevenRoomsMappings(userProfile?.organisation_id);
  const { data: venuesData } = useGetSevenRoomsVenues(userProfile?.organisation_id);

  const saveSevenRoomsMappings = useSaveSevenRoomsMappings();
  const deactivateSevenRooms = useDeactivateSevenRooms();
  const updateSevenRooms = useUpdateSevenRooms();
  const updateSevenRoomsSyncSettings = useUpdateSevenRoomsSyncSettings();
  const updateSevenRoomsClientTagSettings = useUpdateSevenRoomsClientTagSettings();

  const [sevenroomsVenues, setSevenRoomsVenues] = useState([]);
  const [bconnectBrands, setBconnectBrands] = useState([]);
  const [independentLocations, setIndependentLocations] = useState([]);
  const [locationMappings, setLocationMappings] = useState({});
  const [originalLocationMappings, setOriginalLocationMappings] = useState({});
  const [brandListSelections, setBrandListSelections] = useState({});
  const originalLocationMappingsRef = useRef(originalLocationMappings);

  useEffect(() => {
    originalLocationMappingsRef.current = originalLocationMappings;
  }, [originalLocationMappings]);

  const handleSaveCredentials = async () => {
    if (!userProfile?.organisation_id) {
      setToastMessage("Organisation ID missing");
      return;
    }

    const nextSecret = clientSecret.includes("*") ? "" : clientSecret.trim();
    if (!clientId.trim()) {
      setCredentialsValid(false);
      setCredentialsMessage("Please enter a valid client ID.");
      return;
    }

    setCredentialsValid(true);
    setCredentialsMessage(null);
    setIsCredentialsSaving(true);
    try {
      const payload = {
        organisation_id: userProfile.organisation_id,
        client_id: clientId.trim(),
        environment,
      };
      if (nextSecret) payload.client_secret = nextSecret;

      const res = await updateSevenRooms.mutateAsync(payload);
      if (res?.data?.status === "success") {
        queryClient.invalidateQueries(["sevenrooms-venues", userProfile.organisation_id]);
        queryClient.invalidateQueries(["sevenrooms-mappings", userProfile.organisation_id]);
        setCredentialsValid(true);
        setToastMessage("SevenRooms credentials validated and saved");
      } else {
        const msg = "Failed to update credentials. Please try again shortly or contact support.";
        setCredentialsValid(false);
        setCredentialsMessage(msg);
        setToastMessage(msg);
      }
    } catch (err) {
      const msg = "Failed to update credentials. Please try again shortly or contact support.";
      setCredentialsValid(false);
      setCredentialsMessage(msg);
      setToastMessage(msg);
    } finally {
      setIsCredentialsSaving(false);
    }
  };

  const saveSyncSettings = async (
    nextPushEnabled,
    nextPullEnabled,
    pullScope = null,
    pushScope = null
  ) => {
    if (!userProfile?.organisation_id) {
      setToastMessage("Organisation ID missing");
      return;
    }
    if (!isOwner) {
      setShowPermissionAlert(true);
      return;
    }
    if (!nextPushEnabled && !nextPullEnabled) {
      setToastMessage("Enable push, pull, or both for the SevenRooms integration.");
      return;
    }
    setIsSyncSettingsSaving(true);
    try {
      const payload = {
        organisation_id: userProfile.organisation_id,
        push_enabled: nextPushEnabled,
        pull_enabled: nextPullEnabled,
      };
      if (nextPushEnabled && pushScope) {
        payload.push_scope = pushScope;
      }
      if (nextPullEnabled && pullScope) {
        payload.pull_scope = pullScope;
      }
      const res = await updateSevenRoomsSyncSettings.mutateAsync(payload);
      if (res?.data?.status === "success") {
        setPushEnabled(Boolean(res.data.push_enabled));
        setPullEnabled(Boolean(res.data.pull_enabled));
        queryClient.invalidateQueries(["sevenrooms-mappings", userProfile.organisation_id]);
        setToastMessage("SevenRooms sync directions updated");
      } else {
        setToastMessage("Failed to update sync directions");
      }
    } catch (err) {
      setToastMessage(err?.response?.data?.message || "Failed to update sync directions");
    } finally {
      setIsSyncSettingsSaving(false);
    }
  };

  const handlePushToggle = (checked) => {
    if (!checked) {
      saveSyncSettings(false, pullEnabled);
      return;
    }
    setPushScopeDialogOpen(true);
  };

  const handlePushScopeChosen = (scope) => {
    setPushScopeDialogOpen(false);
    saveSyncSettings(true, pullEnabled, null, scope);
  };

  const handlePullToggle = (checked) => {
    if (!checked) {
      saveSyncSettings(pushEnabled, false);
      return;
    }
    setPullScopeDialogOpen(true);
  };

  const handlePullScopeChosen = (scope) => {
    setPullScopeDialogOpen(false);
    saveSyncSettings(pushEnabled, true, scope);
  };

  const handleSaveClientTagSettings = async () => {
    if (!userProfile?.organisation_id) {
      setToastMessage("Organisation ID missing");
      return;
    }
    if (!isOwner) {
      setShowPermissionAlert(true);
      return;
    }
    if (Boolean(clientTagGroup.trim()) !== Boolean(clientTagName.trim())) {
      setToastMessage("Enter both the SevenRooms tag group/source name and tag name.");
      return;
    }

    setIsClientTagSaving(true);
    try {
      const res = await updateSevenRoomsClientTagSettings.mutateAsync({
        organisation_id: userProfile.organisation_id,
        client_tag_group: clientTagGroup.trim(),
        client_tag_name: clientTagName.trim(),
      });
      if (res?.data?.status !== "success") {
        throw new Error(res?.data?.message || "Failed to update client tag settings");
      }
      setClientTagGroup(res.data.client_tag_group || "");
      setClientTagName(res.data.client_tag_name || "");
      setOriginalClientTagGroup(res.data.client_tag_group || "");
      setOriginalClientTagName(res.data.client_tag_name || "");
      queryClient.invalidateQueries(["sevenrooms-mappings", userProfile.organisation_id]);
      setToastMessage("SevenRooms client tag settings saved");
    } catch (err) {
      setToastMessage(
        err?.response?.data?.message || err?.message || "Failed to update client tag settings"
      );
    } finally {
      setIsClientTagSaving(false);
    }
  };

  const handleSaveLocationMappings = async (section) => {
    const savingSetters =
      section === "brands"
        ? [setIsBrandsSaving]
        : section === "independent"
        ? [setIsIndependentSaving]
        : [setIsBrandsSaving, setIsIndependentSaving];

    savingSetters.forEach((s) => s(true));

    try {
      const allLocations = [
        ...(independentLocations || []),
        ...(Object.values(brandLocationsMap || {}).flat() || []),
      ];

      const locationPayload = (allLocations || [])
        .map((loc) => {
          const locId = loc.id ?? loc.value ?? loc.location_id;
          const key = String(locId);
          const curr = locationMappings[key] || {};
          const currList = curr.sevenrooms_venue_id || null;
          if (!currList) return null;
          const venueObj = sevenroomsVenues.find((bl) => String(bl.id) === String(currList));
          return {
            bconnect_location_id: locId,
            bconnect_location_name: loc.name ?? loc.label ?? "",
            sevenrooms_venue_id: currList,
            sevenrooms_venue_name: venueObj?.name || "",
          };
        })
        .filter(Boolean);

      const result = await saveSevenRoomsMappings.mutateAsync({
        organisation_id: userProfile.organisation_id,
        location_mappings: locationPayload,
      });

      if (result?.data?.status !== "success") {
        throw new Error(result?.data?.message || "Failed to save location mappings");
      }

      setToastMessage("Location mappings saved successfully");
      queryClient.invalidateQueries(["sevenrooms-mappings", userProfile.organisation_id]);
    } catch (err) {
      console.error("Error saving SevenRooms location mappings:", err);
      setToastMessage(err?.message || "Failed to save location mappings");
    } finally {
      savingSetters.forEach((s) => s(false));
    }
  };

  const handleDeactivate = async () => {
    setIsDeactivating(true);
    try {
      const res = await deactivateSevenRooms.mutateAsync({
        organisation_id: userProfile.organisation_id,
      });
      if (res?.data?.status !== "success") {
        throw new Error(res?.data?.message || "Failed to deactivate integration");
      }
      setIsDeactivating(false);
      setShowConfirmDialog(false);
      setToastMessage("SevenRooms integration disconnected");
      setLocationMappings({});
      setSevenRoomsVenues([]);
      queryClient.invalidateQueries(["sevenrooms-status", userProfile.organisation_id]);
    } catch (err) {
      console.error("Error deactivating SevenRooms:", err);
      setToastMessage(err?.message || "Failed to deactivate SevenRooms integration");
      setIsDeactivating(false);
    }
  };

  useEffect(() => {
    if (!mappingsData) {
      setLocationMappings({});
      setOriginalLocationMappings({});
      originalLocationMappingsRef.current = {};
      return;
    }

    const mappings = mappingsData.data.mappings?.location_mappings || [];
    const serverMap = {};
    mappings.forEach((m) => {
      const key = String(m.bconnect_location_id);
      serverMap[key] = { sevenrooms_venue_id: m.sevenrooms_venue_id || "" };
    });

    const newLocationMappings = {};
    Object.keys(serverMap).forEach((key) => {
      newLocationMappings[key] = {
        sevenrooms_venue_id: serverMap[key].sevenrooms_venue_id,
        isMapped: Boolean(serverMap[key].sevenrooms_venue_id),
      };
    });
    setLocationMappings(newLocationMappings);

    const newOriginals = {};
    Object.keys(serverMap).forEach((k) => {
      newOriginals[k] = { sevenrooms_venue_id: serverMap[k].sevenrooms_venue_id };
    });
    setOriginalLocationMappings(newOriginals);
    originalLocationMappingsRef.current = newOriginals;

    setClientId(mappingsData.data.client_id || "");
    setOriginalClientId(mappingsData.data.client_id || "");
    setClientSecret(mappingsData.data.client_secret || "");
    setOriginalClientSecret(mappingsData.data.client_secret || "");
    setEnvironment(mappingsData.data.environment || "demo");
    setOriginalEnvironment(mappingsData.data.environment || "demo");
    setClientTagGroup(mappingsData.data.client_tag_group || "");
    setClientTagName(mappingsData.data.client_tag_name || "");
    setOriginalClientTagGroup(mappingsData.data.client_tag_group || "");
    setOriginalClientTagName(mappingsData.data.client_tag_name || "");

    const nextFieldMappings = normalizeSevenRoomsFieldMappings(mappingsData.data.field_mappings);
    setFieldMappings(nextFieldMappings);
    setPushEnabled(mappingsData.data.push_enabled !== false);
    setPullEnabled(Boolean(mappingsData.data.pull_enabled));
  }, [mappingsData]);

  useEffect(() => {
    if (venuesData) {
      setSevenRoomsVenues(
        (venuesData.data.venues || []).map((l) => ({ id: String(l.id ?? ""), name: l.name }))
      );
    }
  }, [venuesData]);

  useEffect(() => {
    if (brandsData && locationsData) {
      const defaultBrand = (brandsData || []).find((b) => b.is_default);
      const nonDefault = (brandsData || []).filter((b) => !b.is_default);
      setBconnectBrands(nonDefault || []);
      const defaultLocations = (locationsData || []).filter(
        (loc) => loc.brand_id === defaultBrand?.id || loc.brand_id === null
      );
      setIndependentLocations(defaultLocations || []);
    } else {
      setBconnectBrands([]);
      setIndependentLocations([]);
    }
  }, [brandsData, locationsData]);

  const brandLocationsMap = useMemo(() => {
    const map = {};
    if (!bconnectBrands) return map;
    bconnectBrands.forEach((b) => {
      const sourceBrand = (brandsData || []).find((bd) => String(bd.id) === String(b.id));
      if (sourceBrand && Array.isArray(sourceBrand.locations)) {
        map[b.id] = sourceBrand.locations;
      } else {
        map[b.id] = (locationsData || []).filter((loc) => String(loc.brand_id) === String(b.id));
      }
    });
    return map;
  }, [bconnectBrands, brandsData, locationsData]);

  const allBrandOwnedLocations = useMemo(
    () => Object.values(brandLocationsMap || {}).flat() || [],
    [brandLocationsMap]
  );

  const hasBrandSectionChanges = useMemo(() => {
    return (allBrandOwnedLocations || []).some((loc) => {
      const locId = loc.id ?? loc.value ?? loc.location_id;
      const key = String(locId);
      const curr = locationMappings[key] || {};
      const orig = originalLocationMappings[key] || { sevenrooms_venue_id: "" };
      const currList = String(curr.sevenrooms_venue_id || "");
      const origList = String(orig.sevenrooms_venue_id || "");
      if (!currList) return false;
      return currList !== origList;
    });
  }, [allBrandOwnedLocations, locationMappings, originalLocationMappings]);

  const hasIndependentLocationChanges = useMemo(() => {
    return (independentLocations || []).some((loc) => {
      const locId = loc.id ?? loc.value ?? loc.location_id;
      const key = String(locId);
      const curr = locationMappings[key] || {};
      const orig = originalLocationMappings[key] || { sevenrooms_venue_id: "" };
      const currList = String(curr.sevenrooms_venue_id || "");
      const origList = String(orig.sevenrooms_venue_id || "");
      if (!currList) return false;
      return currList !== origList;
    });
  }, [independentLocations, locationMappings, originalLocationMappings]);

  const hasCredentialsChanges = useMemo(() => {
    const secretChanged = clientSecret !== originalClientSecret && !clientSecret.includes("*");
    return (
      clientId !== originalClientId ||
      environment !== originalEnvironment ||
      secretChanged
    );
  }, [clientId, originalClientId, clientSecret, originalClientSecret, environment, originalEnvironment]);

  const hasClientTagChanges = useMemo(
    () =>
      clientTagGroup.trim() !== originalClientTagGroup ||
      clientTagName.trim() !== originalClientTagName,
    [clientTagGroup, clientTagName, originalClientTagGroup, originalClientTagName]
  );

  const handleLocationVenueChange = (bconnectLocationId, venueId) => {
    const key = String(bconnectLocationId);
    setLocationMappings((prev) => {
      const next = { ...(prev || {}) };
      const existing = next[key] || { sevenrooms_venue_id: "", isMapped: false };
      next[key] = { ...existing, sevenrooms_venue_id: venueId, isMapped: Boolean(venueId) };
      return next;
    });
  };

  const handleBrandListSelectionChange = (brandId, listId) => {
    setBrandListSelections((prev) => ({ ...prev, [String(brandId)]: listId }));
  };

  const handleApplyBrandList = (brandId) => {
    const selectedList = brandListSelections[String(brandId)];
    if (!selectedList) return;
    const brandLocs = brandLocationsMap[brandId] || [];
    setLocationMappings((prev) => {
      const next = { ...(prev || {}) };
      (brandLocs || []).forEach((loc) => {
        const locId = loc.id ?? loc.value ?? loc.location_id;
        const key = String(locId);
        const existing = next[key] || { sevenrooms_venue_id: "", isMapped: false };
        next[key] = { ...existing, sevenrooms_venue_id: selectedList, isMapped: Boolean(selectedList) };
      });
      return next;
    });
  };

  const handleDisconnectClick = () => {
    if (isOwner) {
      setShowConfirmDialog(true);
    } else {
      setShowPermissionAlert(true);
    }
  };

  if (!initialItems) {
    return (
      <Flex direction="column" align="center" justify="center" style={{ height: "200px" }}>
        <Text>No locations available.</Text>
      </Flex>
    );
  }

  if (isBrandsLoading) {
    return (
      <Flex direction="column" align="center" justify="center" style={{ height: "200px" }}>
        <Text>Loading SevenRooms integration data...</Text>
      </Flex>
    );
  }

  return (
    <>
      <ActiveIntegrateCard.Root
        name="SevenRooms"
        subtitle="Map locations to SevenRooms venues"
        image_url="sevenrooms.jpeg"
        option={{
          text: "Disconnect",
          color: "red",
          onClick: handleDisconnectClick,
        }}
      >
        <ActiveIntegrateCard.Section
          name="API credentials"
          subtitle="Update the SevenRooms client ID, secret, and environment"
        >
          <div className="sr-stack">
            <div className="sr-field">
              <Text size="2" weight="medium" as="label">
                Client ID
              </Text>
              <TextField.Root
                value={clientId}
                onChange={(event) => {
                  setClientId(event.target.value.trim());
                  setCredentialsValid(true);
                }}
                placeholder="Client ID"
                disabled={!isOwner}
                size="2"
              />
            </div>
            <div className="sr-field">
              <Text size="2" weight="medium" as="label">
                Client secret
              </Text>
              <TextField.Root
                type="password"
                value={clientSecret}
                onChange={(event) => {
                  setClientSecret(event.target.value.trim());
                  setCredentialsValid(true);
                }}
                placeholder="Client secret"
                disabled={!isOwner}
                size="2"
              />
            </div>
            <div className="sr-field">
              <Text size="2" weight="medium" as="label">
                Environment
              </Text>
              <Select.Root
                value={environment}
                onValueChange={setEnvironment}
                disabled={!isOwner}
                size="2"
              >
                <Select.Trigger style={{ width: "100%" }} variant="surface" />
                <Select.Content>
                  {ENVIRONMENT_OPTIONS.map((option) => (
                    <Select.Item key={option.value} value={option.value}>
                      {option.label}
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Root>
            </div>
            <Flex justify="end">
              <Button
                size="1"
                variant="solid"
                color="blue"
                onClick={handleSaveCredentials}
                loading={isCredentialsSaving}
                disabled={!isOwner || isCredentialsSaving || !hasCredentialsChanges}
              >
                Save changes
              </Button>
            </Flex>
            {!credentialsValid && (
              <Text size="1" weight="regular" color="red">
                {credentialsMessage || "Please enter valid credentials."}
              </Text>
            )}
          </div>
        </ActiveIntegrateCard.Section>

        <ActiveIntegrateCard.Section
          name="Client tag"
          subtitle="Optionally identify contacts sent by this integration with an existing SevenRooms client tag"
        >
          <div className="sr-stack">
            <SevenRoomsClientTagSection
              tagGroup={clientTagGroup}
              tagName={clientTagName}
              onTagGroupChange={setClientTagGroup}
              onTagNameChange={setClientTagName}
              disabled={!isOwner}
            />
            <Flex justify="end">
              <Button
                size="1"
                variant="solid"
                color="blue"
                onClick={handleSaveClientTagSettings}
                loading={isClientTagSaving}
                disabled={!isOwner || isClientTagSaving || !hasClientTagChanges}
              >
                Save changes
              </Button>
            </Flex>
          </div>
        </ActiveIntegrateCard.Section>

        <ActiveIntegrateCard.Section
          name="Sync directions"
          subtitle="Choose push only, pull only, or both. At least one direction must remain enabled."
        >
          <SevenRoomsSyncDirectionsSection
            pushChecked={pushEnabled}
            pullChecked={pullEnabled}
            disabled={!isOwner || isSyncSettingsSaving}
            onPushCheckedChange={handlePushToggle}
            onPullCheckedChange={handlePullToggle}
          />
        </ActiveIntegrateCard.Section>

        <ActiveIntegrateCard.Section
          name="Data fields"
          subtitle="Captive portal fields are mapped to SevenRooms client columns. Fields a guest did not enter are skipped automatically."
        >
          <div className="sr-stack">
            <SevenRoomsRequiredFieldsNotice />
            <SevenRoomsDataFieldsSection fieldMappings={fieldMappings} />
          </div>
        </ActiveIntegrateCard.Section>

        <ActiveIntegrateCard.Section name="Brands" subtitle="Group: b connect brands with each of their locations">
          <div className="sr-stack">
            <SevenRoomsLocationPairHeaders />

            {(bconnectBrands || []).length > 0 ? (
              (bconnectBrands || []).map((b) => {
                const brandLocations = brandLocationsMap[b.id] || [];
                return (
                  <Flex key={b.id} direction="column" gap="3" style={{ width: "100%", minWidth: 0 }}>
                    <Flex direction="row" wrap="wrap" style={{ width: "100%", alignItems: "center", gap: 8 }}>
                      <Text size="2" weight="medium" style={{ flex: "1 1 8rem", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        [{b.name}]
                      </Text>
                      {(brandLocations || []).length > 0 && (
                        <Flex style={{ gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                          <Select.Root
                            value={brandListSelections[String(b.id)] || ""}
                            onValueChange={(v) => handleBrandListSelectionChange(b.id, v)}
                            disabled={!isOwner}
                          >
                            <Select.Trigger placeholder="Apply venue to brand..." style={{ width: "100%", minWidth: 160, maxWidth: 220, boxSizing: "border-box" }}>
                              <div
                                style={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", width: "100%" }}
                                title={brandListSelections[String(b.id)] ? (sevenroomsVenues.find((bl) => String(bl.id) === String(brandListSelections[String(b.id)]))?.name || "") : "Apply venue to brand..."}
                              >
                                {brandListSelections[String(b.id)] ? (sevenroomsVenues.find((bl) => String(bl.id) === String(brandListSelections[String(b.id)]))?.name || "") : "Apply venue to brand..."}
                              </div>
                            </Select.Trigger>
                            <Select.Content side="bottom" align="start" sideOffset={4}>
                              {sevenroomsVenues.map((bl) => <Select.Item key={bl.id} value={bl.id} title={bl.name}>{bl.name}</Select.Item>)}
                            </Select.Content>
                          </Select.Root>
                          <Button size="1" variant="soft" onClick={() => handleApplyBrandList(b.id)} disabled={!isOwner || !brandListSelections[String(b.id)]}>
                            Apply
                          </Button>
                        </Flex>
                      )}
                    </Flex>
                    {(brandLocations || []).length > 0 ? (
                      brandLocations.map((loc) => {
                        const locId = loc.id ?? loc.value ?? loc.location_id;
                        const key = String(locId);
                        const mapping = locationMappings[key] || {};
                        const locationName = loc.name ?? loc.label ?? "";
                        return (
                          <SevenRoomsLocationPair
                            key={key}
                            left={<SevenRoomsReadonlyField title={locationName}>{locationName}</SevenRoomsReadonlyField>}
                            right={
                              <Select.Root value={mapping.sevenrooms_venue_id || ""} onValueChange={(v) => handleLocationVenueChange(key, v)} disabled={!isOwner}>
                                <Select.Trigger placeholder="Select..." style={{ width: "100%", boxSizing: "border-box" }}>
                                  <div style={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", width: "100%" }} title={mapping.sevenrooms_venue_id ? (sevenroomsVenues.find((bl) => String(bl.id) === String(mapping.sevenrooms_venue_id))?.name || "") : "Select a SevenRooms venue"}>
                                    {mapping.sevenrooms_venue_id ? (sevenroomsVenues.find((bl) => String(bl.id) === String(mapping.sevenrooms_venue_id))?.name || "") : "Select a SevenRooms venue"}
                                  </div>
                                </Select.Trigger>
                                <Select.Content side="bottom" align="start" sideOffset={4}>
                                  {sevenroomsVenues.map((bl) => <Select.Item key={bl.id} value={bl.id} title={bl.name}>{bl.name}</Select.Item>)}
                                </Select.Content>
                              </Select.Root>
                            }
                          />
                        );
                      })
                    ) : (
                      <Flex justify="center" style={{ padding: "8px 0 16px 0" }}>
                        <Text size="2" style={{ color: "var(--gray-a9)" }}>No locations for this brand</Text>
                      </Flex>
                    )}
                  </Flex>
                );
              })
            ) : (
              <Flex justify="center" style={{ padding: "20px 0" }}>
                <Text>No brands found.</Text>
              </Flex>
            )}

            <Flex justify="end">
              <Button
                size="2"
                variant="solid"
                color="blue"
                onClick={() => handleSaveLocationMappings("brands")}
                loading={isBrandsSaving}
                disabled={!isOwner || isBrandsSaving || !hasBrandSectionChanges}
              >
                Save changes
              </Button>
            </Flex>
          </div>
        </ActiveIntegrateCard.Section>

        <ActiveIntegrateCard.Section name="Locations" subtitle="Map independent b connect locations to a SevenRooms venue">
          <div className="sr-stack">
            <SevenRoomsLocationPairHeaders />

            {(independentLocations || []).length > 0 ? (
              (independentLocations || []).map((loc) => {
                const locId = loc.id ?? loc.value ?? loc.location_id;
                const key = String(locId);
                const mapping = locationMappings[key] || {};
                const locationName = loc.name ?? loc.label ?? "";
                return (
                  <SevenRoomsLocationPair
                    key={key}
                    left={<SevenRoomsReadonlyField title={locationName}>{locationName}</SevenRoomsReadonlyField>}
                    right={
                      <Select.Root value={mapping.sevenrooms_venue_id || ""} onValueChange={(v) => handleLocationVenueChange(key, v)} disabled={!isOwner}>
                        <Select.Trigger placeholder="Select..." style={{ width: "100%", boxSizing: "border-box" }}>
                          <div style={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", width: "100%" }} title={mapping.sevenrooms_venue_id ? (sevenroomsVenues.find((bl) => String(bl.id) === String(mapping.sevenrooms_venue_id))?.name || "") : "Select a SevenRooms venue"}>
                            {mapping.sevenrooms_venue_id ? (sevenroomsVenues.find((bl) => String(bl.id) === String(mapping.sevenrooms_venue_id))?.name || "") : "Select a SevenRooms venue"}
                          </div>
                        </Select.Trigger>
                        <Select.Content side="bottom" align="start" sideOffset={4}>
                          {sevenroomsVenues.map((bl) => <Select.Item key={bl.id} value={bl.id} title={bl.name}>{bl.name}</Select.Item>)}
                        </Select.Content>
                      </Select.Root>
                    }
                  />
                );
              })
            ) : (
              <Flex justify="center" style={{ padding: "20px 0" }}>
                <Text>No locations found.</Text>
              </Flex>
            )}

            <Flex justify="end">
              <Button
                size="2"
                variant="solid"
                color="blue"
                onClick={() => handleSaveLocationMappings("independent")}
                loading={isIndependentSaving}
                disabled={!isOwner || isIndependentSaving || !hasIndependentLocationChanges}
              >
                Save changes
              </Button>
            </Flex>
          </div>
        </ActiveIntegrateCard.Section>
      </ActiveIntegrateCard.Root>

      <Dialog.Root open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <Dialog.Content size="3" style={{ maxWidth: 450 }}>
          <Dialog.Title>Disconnect SevenRooms</Dialog.Title>
          <Dialog.Description size="2" mb="4">Customer data will stop syncing with SevenRooms.</Dialog.Description>
          <Flex gap="3" justify="end">
            <Dialog.Close>
              <Button variant="soft" color="gray">Cancel</Button>
            </Dialog.Close>
            <Button variant="solid" color="red" onClick={handleDeactivate} loading={isDeactivating} disabled={isDeactivating}>Disconnect</Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>

      <PermissionAlertDialog
        isOpen={showPermissionAlert}
        onOpenChange={setShowPermissionAlert}
        message="Only owners can perform this action"
      />

      <SevenRoomsPullScopeDialog
        isOpen={pullScopeDialogOpen}
        onOpenChange={setPullScopeDialogOpen}
        onChoose={handlePullScopeChosen}
      />

      <SevenRoomsPushScopeDialog
        isOpen={pushScopeDialogOpen}
        onOpenChange={setPushScopeDialogOpen}
        onChoose={handlePushScopeChosen}
      />
    </>
  );
}
